import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import * as Icons from "lucide-react";
import BannerCarousel from "@/components/BannerCarousel";
import SectionHeader from "@/components/SectionHeader";
import usePublicBanners from "@/lib/usePublicBanners";
import {
  SERVICES,
  WHY_DOMAINS,
  SOFTWARE_PRODUCTS,
  INDUSTRIES,
  DOMAINS,
  TOTAL_PORTFOLIO_VALUE,
} from "@/data/site";

const formatUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function IconBlock({ name, className = "" }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.5} />;
}

export default function Home() {
  const { hasBanner } = usePublicBanners();
  return (
    <>
      <BannerCarousel />

      {/* ============== HERO ============== */}
      <section
        data-testid="hero-section"
        className={`relative ${hasBanner ? "pt-4 pb-20 md:pt-6 md:pb-28" : "pt-32 pb-24 md:pt-40 md:pb-32"} overflow-hidden bg-white`}
      >
        <div className="absolute inset-0 grid-bg radial-mask opacity-50" />
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "radial-gradient(800px 400px at 30% 20%, rgba(0,102,255,0.14), transparent), radial-gradient(600px 300px at 80% 80%, rgba(200,150,31,0.06), transparent)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <div className="eyebrow mb-6 inline-flex items-center gap-2">
                <Sparkles size={12} /> S&amp;B TECHNOLOGIES · EST. 2026
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                Software Development.
                <br />
                <span className="text-[#0066FF]">Digital Assets.</span>
                <br />
                Technology Growth.
              </h1>
              <p className="mt-8 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
                We build software platforms, digital business systems, domain portfolios, and
                technology solutions designed to help businesses grow, automate, generate leads,
                and dominate their markets.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/services" className="btn-primary" data-testid="hero-view-services-btn">
                  View Our Services <ArrowRight size={16} />
                </Link>
                <Link
                  to="/domain-portfolio"
                  className="btn-secondary"
                  data-testid="hero-domain-portfolio-btn"
                >
                  Explore Portfolio <ArrowUpRight size={16} />
                </Link>
              </div>

              {/* Stats strip */}
              <div className="mt-14 pt-8 border-t border-slate-200 grid grid-cols-3 gap-6 max-w-xl">
                {[
                  { v: "$670K", l: "Portfolio Value" },
                  { v: "13+", l: "Software Concepts" },
                  { v: "10", l: "Industries Served" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-2xl md:text-3xl font-semibold text-slate-900">
                      {s.v}
                    </div>
                    <div className="mt-1 text-[0.65rem] font-mono uppercase tracking-[0.2em] text-slate-500">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero card */}
            <div className="lg:col-span-5 animate-fade-in-up delay-200">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-[#0066FF]/20 via-transparent to-[#C8961F]/10 blur-xl" />
                <div className="relative tracing-card p-8 md:p-10">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="eyebrow mb-2">// LIVE_OPPORTUNITY</div>
                      <h3 className="text-xl font-semibold text-slate-900">
                        Arizona Real Estate Domain Portfolio
                      </h3>
                    </div>
                    <span className="font-mono text-xs text-[#C8961F] uppercase tracking-wider">
                      Featured
                    </span>
                  </div>

                  <div className="space-y-2.5 mb-6">
                    {DOMAINS.slice(0, 5).map((d) => (
                      <div
                        key={d.name}
                        className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0"
                      >
                        <span className="text-slate-700 font-mono text-xs">{d.name}</span>
                        <span className="text-[#C8961F] font-medium">{formatUSD(d.value)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-[#0066FF]/20">
                    <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                      Certified Total
                    </span>
                    <span className="font-display text-2xl font-semibold text-slate-900">
                      {formatUSD(TOTAL_PORTFOLIO_VALUE)}
                    </span>
                  </div>

                  <Link
                    to="/domain-portfolio"
                    data-testid="hero-portfolio-card-cta"
                    className="mt-6 btn-gold w-full justify-center text-sm"
                  >
                    View Full Portfolio <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== ABOUT ============== */}
      <section className="py-24 md:py-32 relative bg-slate-50 border-y border-slate-100" id="about">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">01 — About</div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
                A technology company building the infrastructure of digital business.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6">
              <p className="text-base md:text-lg text-slate-700 leading-relaxed">
                S&amp;B Technologies was founded by{" "}
                <span className="text-slate-900 font-medium">Shawn Williams</span> and{" "}
                <span className="text-slate-900 font-medium">Brittney Williams</span> as a
                technology company focused on software development, digital transformation,
                consulting, domain strategy, and online business growth.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                We help businesses create systems that generate leads, manage operations,
                automate workflows, launch websites, build SaaS products, and turn digital
                ideas into scalable platforms.
              </p>
              <div className="grid grid-cols-2 gap-6 pt-6">
                {[
                  { v: "Build", d: "Custom software, SaaS, and digital platforms" },
                  { v: "Acquire", d: "Premium domain portfolios and digital assets" },
                  { v: "Consult", d: "Growth strategy, operations, and automation" },
                  { v: "Scale", d: "Lead generation systems and market dominance" },
                ].map((c) => (
                  <div key={c.v} className="border-l border-[#0066FF]/30 pl-4">
                    <div className="font-display text-lg font-semibold text-slate-900">{c.v}</div>
                    <div className="mt-1 text-sm text-slate-500">{c.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== SERVICES ============== */}
      <section
        className="py-24 md:py-32 relative border-t border-slate-100"
        id="services"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <SectionHeader
              eyebrow="02 — Services"
              title="Built for businesses that need to move fast."
              description="From custom software to digital asset strategy, our services are engineered to deliver measurable growth across every channel."
            />
            <Link
              to="/services"
              className="btn-secondary self-start lg:self-end shrink-0"
              data-testid="services-view-all-btn"
            >
              All Services <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Link
                key={s.id}
                to={`/services#${s.id}`}
                data-testid={`service-card-${s.id}`}
                className="tracing-card p-8 group"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex h-12 w-12 items-center justify-center border border-[#0066FF]/30 bg-white text-[#0066FF] group-hover:border-[#0066FF] group-hover:shadow-[0_0_15px_rgba(0,102,255,0.3)] transition-all">
                    <IconBlock name={s.icon} className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-3 group-hover:text-[#0066FF] transition-colors">
                  {s.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.short}</p>
                <div className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#0066FF] opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowUpRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============== DOMAIN PORTFOLIO ============== */}
      <section
        className="py-24 md:py-32 relative bg-slate-50 border-y border-slate-100"
        id="portfolio"
        data-testid="domain-portfolio-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="03 — Portfolio"
            title="Premium Digital Real Estate Portfolio"
            description="S&B Technologies owns, consults on, and sells premium domain assets designed to create online authority, lead generation power, and long-term digital value."
          />

          <div className="mt-12 tracing-card p-6 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <div className="eyebrow">// Featured Portfolio</div>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-slate-900">
                  Arizona Real Estate Domain Portfolio
                </h3>
              </div>
              <div className="text-right">
                <div className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-slate-500">
                  Certified Total Value
                </div>
                <div className="font-display text-3xl md:text-4xl font-semibold text-[#C8961F]">
                  {formatUSD(TOTAL_PORTFOLIO_VALUE)}
                </div>
              </div>
            </div>

            <div className="border-t border-b border-slate-200 divide-y divide-slate-100">
              {DOMAINS.map((d, idx) => (
                <div
                  key={d.name}
                  className="grid grid-cols-12 gap-4 py-4 items-center hover:bg-slate-50 transition-colors"
                  data-testid={`home-domain-row-${idx}`}
                >
                  <div className="col-span-1 font-mono text-xs text-slate-400">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-7 md:col-span-8">
                    <div className="font-mono text-sm md:text-base text-slate-900">{d.name}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Certified Estimated Value
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-3 text-right font-display text-lg md:text-xl font-semibold text-[#C8961F]">
                    {formatUSD(d.value)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-[#0066FF]/20 p-5 bg-slate-50/80">
                <div className="eyebrow mb-2">// Acquisition Note</div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  S&amp;B Technologies is accepting offers on the{" "}
                  <span className="text-slate-900 font-medium">entire portfolio only</span>.
                  Individual domains are not currently being offered separately.
                </p>
              </div>
              <div className="border border-[#C8961F]/30 p-5 bg-[#C8961F]/[0.03]">
                <div className="eyebrow mb-2" style={{ color: "#C8961F" }}>
                  // Partnership Opportunity
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Strategic investment available at{" "}
                  <span className="text-[#C8961F] font-medium">$250,000</span> · 50% down ·
                  balance over 12 months.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/domain-portfolio"
                className="btn-gold"
                data-testid="home-portfolio-full-cta"
              >
                View Full Portfolio Details <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="btn-secondary"
                data-testid="home-portfolio-offer-cta"
              >
                Submit Acquisition Offer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============== WHY PREMIUM DOMAINS ============== */}
      <section className="py-24 md:py-32 relative border-t border-slate-100" id="why">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="04 — Thesis"
            title="Why premium domains matter."
            description="Premium domains are digital real estate. They are scarce, memorable, searchable, brandable, and capable of generating long-term value. Exact-match and geo-targeted domains help companies capture high-intent traffic, reduce advertising costs, improve trust, and increase conversion potential."
          />

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_DOMAINS.map((w, i) => (
              <div
                key={w.title}
                className="tracing-card p-6 group"
                data-testid={`why-domain-card-${i}`}
              >
                <div className="flex h-11 w-11 items-center justify-center border border-[#0066FF]/30 text-[#0066FF] mb-5 group-hover:border-[#0066FF]">
                  <IconBlock name={w.icon} className="h-5 w-5" />
                </div>
                <h3 className="font-display text-base font-semibold text-slate-900">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SOFTWARE PORTFOLIO ============== */}
      <section
        className="py-24 md:py-32 relative bg-slate-50 border-y border-slate-100"
        id="software"
        data-testid="software-portfolio-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <SectionHeader
              eyebrow="05 — Software"
              title="Software portfolio & product concepts."
              description="S&B Technologies develops and supports a deep stack of software concepts — from advisory platforms to media networks and marketplaces."
            />
            <Link
              to="/software-portfolio"
              className="btn-secondary self-start lg:self-end shrink-0"
              data-testid="software-view-all-btn"
            >
              Full Software Portfolio <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {SOFTWARE_PRODUCTS.map((p, i) => (
              <div
                key={p.name}
                className="tracing-card p-5 group"
                data-testid={`software-card-${i}`}
              >
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-slate-500 mb-3">
                  {p.category}
                </div>
                <div className="font-display text-base font-semibold text-slate-900 group-hover:text-[#0066FF] transition-colors">
                  {p.name}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span
                    className={`text-[0.6rem] font-mono uppercase tracking-[0.15em] px-2 py-1 border ${
                      p.stage === "Development"
                        ? "border-[#0066FF]/40 text-[#0066FF]"
                        : p.stage === "Prototype"
                        ? "border-[#C8961F]/40 text-[#C8961F]"
                        : "border-slate-200 text-slate-500"
                    }`}
                  >
                    {p.stage}
                  </span>
                  <ArrowUpRight
                    size={14}
                    className="text-slate-400 group-hover:text-[#0066FF] transition-colors"
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 max-w-3xl">
            Note — Some platforms are in development, prototype, concept, or portfolio stages.
          </p>
        </div>
      </section>

      {/* ============== INDUSTRIES ============== */}
      <section
        className="py-24 md:py-32 relative border-t border-slate-100"
        id="industries"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="06 — Industries"
            title="Industries we serve."
            description="From local operators to startups disrupting national categories, S&B Technologies powers growth across every vertical."
          />

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind.name}
                className="tracing-card p-6 flex flex-col items-center text-center group"
                data-testid={`industry-card-${i}`}
              >
                <div className="flex h-12 w-12 items-center justify-center border border-[#0066FF]/30 text-[#0066FF] mb-4 group-hover:border-[#0066FF]">
                  <IconBlock name={ind.icon} className="h-5 w-5" />
                </div>
                <div className="font-display text-sm font-semibold text-slate-900">{ind.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== INVESTOR / PARTNERSHIP ============== */}
      <section
        className="py-24 md:py-32 relative bg-slate-50 border-y border-slate-100"
        id="partner"
        data-testid="partner-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="tracing-card p-10 md:p-16 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background:
                  "radial-gradient(600px 300px at 0% 0%, rgba(0,102,255,0.18), transparent), radial-gradient(500px 250px at 100% 100%, rgba(200,150,31,0.12), transparent)",
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-6">
                <div className="eyebrow mb-4">07 — Partnership</div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
                  Partner with S&amp;B Technologies.
                </h2>
                <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
                  S&amp;B Technologies works with investors, business owners, domain buyers,
                  startups, and strategic partners looking to build, acquire, or monetize
                  digital assets and software platforms.
                </p>
                <Link
                  to="/investors"
                  className="mt-8 btn-gold"
                  data-testid="partner-cta-btn"
                >
                  Request Partnership Information <ArrowRight size={16} />
                </Link>
              </div>
              <div className="lg:col-span-6">
                <div className="eyebrow mb-4">// Opportunities Include</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Software builds",
                    "Domain portfolio acquisitions",
                    "Lead-generation projects",
                    "SaaS development",
                    "Marketing automation",
                    "Business consulting",
                    "Revenue-share projects",
                    "Digital asset sales",
                  ].map((o) => (
                    <div
                      key={o}
                      className="flex items-center gap-3 border border-slate-200 p-3 hover:border-[#0066FF]/40 transition-colors"
                    >
                      <span className="h-1.5 w-1.5 bg-[#0066FF]" />
                      <span className="text-sm text-slate-700">{o}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
